import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { Icon, Loading } from '@cotera/client/app/components';
import { useWorkOSDataProviders } from '@cotera/client/app/hooks/use-workos-data-providers';
import { Button } from '@cotera/client/v0/components/ui/button';
import { openSafeUrl } from '@cotera/client/app/etc/safe-url';

export interface OAuthConnectButtonProps {
  /** The display name shown on the button (e.g., "Slack", "Notion") */
  displayName: string;
  /** The provider key used for the WorkOS API (e.g., "slack", "notion") */
  provider?: string;
  /** Callback when the OAuth connection completes */
  onConnected: (event: OAuthConnectedEvent) => void;
  /**
   * If true, fire onConnected immediately when an existing connection is detected
   * on initial render. Defaults to false.
   */
  notifyOnAlreadyConnected?: boolean;
  /** Optional class name for styling */
  className?: string;
}

export type OAuthConnectedEvent = {
  connectionState: 'newly-connected' | 'already-connected';
};

const POLL_INTERVAL_MS = 500;

/**
 * OAuth connection button that uses WorkOS Data Providers API directly.
 *
 * Suspends while loading initial provider status.
 * Once loaded, if already connected, fires onConnected immediately only when
 * notifyOnAlreadyConnected is enabled.
 * If not connected, shows a connect button and polls while the popup is open.
 */
export const OAuthConnectButton: React.FC<OAuthConnectButtonProps> = ({
  displayName,
  provider,
  onConnected,
  notifyOnAlreadyConnected = false,
  className: _className,
}) => {
  const { isLoading: authLoading, user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(false);
  const hasTriggeredRef = useRef(false);
  const popupRef = useRef<Window | null>(null);

  const providerKey = provider ?? displayName.toLowerCase();

  const { providers, getAuthorizationUrl } = useWorkOSDataProviders({
    refetchInterval: isConnecting ? POLL_INTERVAL_MS : false,
  });

  const isConnected = providers.some(
    (p) =>
      p.provider.toLowerCase() === providerKey.toLowerCase() &&
      p.status === 'connected'
  );

  // When connection detected, stop polling and fire callback
  useEffect(() => {
    if (
      isConnected &&
      !hasTriggeredRef.current &&
      (isConnecting || notifyOnAlreadyConnected)
    ) {
      hasTriggeredRef.current = true;
      setIsConnecting(false);
      onConnected({ connectionState: 'newly-connected' });
    }
  }, [isConnected, isConnecting, notifyOnAlreadyConnected, onConnected]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(false);
    try {
      const result = await getAuthorizationUrl(providerKey);

      if ('alreadyConnected' in result) {
        setIsConnecting(false);
        if (!hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          onConnected({ connectionState: 'already-connected' });
        }
        return;
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // `authorizationUrl` comes back from the WorkOS API; refuse anything
      // that isn't plain http(s) rather than handing it to the browser.
      popupRef.current = openSafeUrl(
        result.authorizationUrl,
        `oauth-${providerKey}`,
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (popupRef.current === null) {
        setIsConnecting(false);
        setError(true);
      }
    } catch {
      setIsConnecting(false);
      setError(true);
    }
  };

  if (authLoading) {
    return <Loading.Spinner variant="sm" />;
  }

  if (user === null) {
    return <p className="text-sm text-muted-foreground">Sign in to connect</p>;
  }

  if (isConnected) {
    return (
      <p className="text-sm text-muted-foreground">
        Connected to {displayName}
      </p>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2">
        <p className="text-sm text-destructive">Connection error</p>
        <button
          onClick={() => {
            setError(false);
          }}
          className="text-sm text-primary underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Button size="sm" onClick={handleConnect} disabled={isConnecting}>
      {isConnecting ? (
        <>
          <Loading.Spinner variant="sm" />
          Connecting...
        </>
      ) : (
        `${displayName}`
      )}
      <Icon icon="arrow-up-right" />
    </Button>
  );
};
